import { electron } from 'lib-shared-electron';
import { useCallback } from 'react';

import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { getLocalStorageDesktopAppNoticeCache } from '../../../localStorageDesktopAppNoticeCache/localStorageDesktopAppNoticeCache';

import { isDesktopAppCacheInvalidOrExpired } from './utils/isDesktopAppCacheInvalidOrExpired';
import { isDesktopAppVersionBelow } from './utils/isDesktopAppVersionBelow';
import { isDesktopAppVersionUnsupported } from './utils/isDesktopAppVersionUnsupported';

// Which versions of releases that should see this modal
const showForBelowVersion = '4.0.6';
// If the modal should also be shown for MSI standalone builds
const showForMsiStandalone = true;
// How many days until it should show again if cancelled
const daysToPostpone = 0;
// If the cache should be bypassed
const bypassAppNoticeCache = true;

/** The trigger function runs the checks and opens the modal if needed. */
export type Trigger = () => void;

/** A hook that checks if it should and shows a modal for desktop app users */
export const useDesktopAppNotice = (): Trigger => {
  const dispatch = useAppDispatch();

  const trigger = useCallback(async () => {
    if (!electron.isElectron()) {
      return;
    }

    const isStandalone = await electron.isStandaloneBuild();

    if (isStandalone && !showForMsiStandalone) {
      return;
    }

    const currentVersionIsBelow = await isDesktopAppVersionBelow(
      showForBelowVersion,
    );

    if (!currentVersionIsBelow) {
      return;
    }

    if (!bypassAppNoticeCache) {
      const desktopAppNoticeCache = getLocalStorageDesktopAppNoticeCache();
      const desktopAppCacheIsInvalidOrExpired =
        isDesktopAppCacheInvalidOrExpired(desktopAppNoticeCache);

      if (!desktopAppCacheIsInvalidOrExpired) {
        return;
      }
    }

    const isUnsupported = isDesktopAppVersionUnsupported();

    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:desktopAppNotice',
          arg: {
            daysToPostpone: isUnsupported ? 0 : daysToPostpone,
            showForBelowVersion,
          },
        },
      }),
    );
  }, [dispatch]);

  return trigger;
};
